// Sales Activity composer — FE-SCRM-024 + FE-SCRM-059 (target picker).
//
// FE-SCRM-059 swap: the raw UUID inputs for prospect_id / opportunity_id
// (a v1 path explicitly flagged as temporary) are gone. A single
// `<TargetSearchPicker restrict_to=["prospect", "opportunity"]>`
// drives anchor selection — selecting an opportunity auto-fills BOTH
// prospect_id (from the BE-side opportunity row's parent_prospect_id)
// AND opportunity_id; selecting a prospect fills prospect_id only.
//
// Contact picker is a typeahead restricted to the chosen prospect's
// sales contacts via `searchContactsForProspect` (FE-SCRM-053's
// list endpoint with ``?prospect_id=&q=``). The contact field surfaces
// only after a prospect anchor exists.
//
// Bound-rail use-cases (where the host page passes one of
// bound_prospect_id / bound_opportunity_id / bound_contact_id) skip
// the picker entirely — the anchor is implicit.

import { useState } from "react";

import { Modal } from "@/design-system";

import { TargetSearchPicker, type TargetSearchResult } from "../components";

import {
  ACTIVITY_TYPE_META, isContactEventType,
} from "./activityTypeMeta";
import {
  searchContactsForProspect,
  searchProspectsAndOpportunities,
} from "./composerSearch";
import type {
  ActivityCreateInput,
  ActivityPatchInput,
  ActivityType,
} from "./types";


// Convert an ISO 8601 timestamp into the local-time string that an
// ``<input type="datetime-local">`` expects ("YYYY-MM-DDTHH:mm"). The
// create flow already round-trips through ``new Date(local).toISOString()``,
// so edit mode formats local-side to match — otherwise a UTC slice
// would shift the displayed time by the user's TZ offset.
function toLocalInputValue(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    + `T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}


export interface ActivityComposerProps {
  initial_type: ActivityType;
  // When bound from a parent (rail), the composer hides the anchor
  // picker — the anchor is implicit. ``bound_contact_id`` lands as
  // ``contact_id`` on the activity and (for the FE-SCRM-053 Contact
  // detail rail) also defaults the prospect anchor via the host
  // page passing both, so the activity row is reachable from both
  // sides of the relationship.
  bound_prospect_id?: string;
  bound_opportunity_id?: string;
  bound_contact_id?: string;
  // When provided, the modal switches to edit mode: the Type select
  // is disabled (PATCH cannot change the activity type), the anchor
  // picker is hidden (anchor relationships are not editable on
  // patch), fields pre-fill from these initial values, and submit
  // dispatches ``on_submit_patch`` with an ``ActivityPatchInput``
  // instead of the create flow's ``on_submit``.
  editing?: {
    initial_subject: string;
    initial_body: string | null;
    initial_due_at: string | null;
  };
  is_submitting: boolean;
  on_submit: (input: ActivityCreateInput) => void;
  on_submit_patch?: (patch: ActivityPatchInput) => void;
  on_cancel: () => void;
}


export function ActivityComposerModal({
  initial_type,
  bound_prospect_id,
  bound_opportunity_id,
  bound_contact_id,
  editing,
  is_submitting,
  on_submit,
  on_submit_patch,
  on_cancel,
}: ActivityComposerProps) {
  const is_editing = !!editing;
  const [type, setType] = useState<ActivityType>(initial_type);
  const [subject, setSubject] = useState(editing?.initial_subject ?? "");
  const [body, setBody] = useState(editing?.initial_body ?? "");
  const [dueAt, setDueAt] = useState(
    editing?.initial_due_at ? toLocalInputValue(editing.initial_due_at) : "",
  );
  // FE-SCRM-059 — picker-driven anchor selection. When a producer
  // picks an opportunity row, ``parent_prospect_id`` lands here as
  // well so the submit payload carries BOTH ids.
  const [anchor, setAnchor] = useState<TargetSearchResult | null>(null);
  const [contact, setContact] = useState<TargetSearchResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const is_anchor_bound = Boolean(
    bound_prospect_id || bound_opportunity_id || bound_contact_id,
  );

  // The contact picker's anchor is the chosen prospect — derived
  // from the bound id, the picker's opportunity selection
  // (parent_prospect_id), or the picker's prospect selection.
  const effective_prospect_id: string | null = (
    bound_prospect_id
    ?? (anchor?.kind === "opportunity" ? anchor.parent_prospect_id ?? null : null)
    ?? (anchor?.kind === "prospect" ? anchor.id : null)
  );

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!subject.trim()) {
      setError("Subject is required.");
      return;
    }

    // Edit mode: build a patch and skip anchor validation entirely —
    // the PATCH endpoint only accepts subject/body/due_at/owner/contact
    // and the anchor isn't reachable from this UI.
    if (is_editing) {
      setError(null);
      const patch: ActivityPatchInput = {
        subject: subject.trim(),
        body: body.trim() || null,
        due_at: dueAt ? new Date(dueAt).toISOString() : null,
      };
      on_submit_patch?.(patch);
      return;
    }

    if (!is_anchor_bound && !anchor) {
      setError("Pick a Prospect or Opportunity to anchor this activity.");
      return;
    }
    setError(null);

    const input: ActivityCreateInput = {
      type, subject: subject.trim(),
      body: body.trim() || null,
      due_at: dueAt ? new Date(dueAt).toISOString() : null,
    };

    if (bound_prospect_id) {
      input.prospect_id = bound_prospect_id;
    } else if (anchor?.kind === "prospect") {
      input.prospect_id = anchor.id;
    } else if (anchor?.kind === "opportunity") {
      input.opportunity_id = anchor.id;
      if (anchor.parent_prospect_id) {
        input.prospect_id = anchor.parent_prospect_id;
      }
    }

    if (bound_opportunity_id) input.opportunity_id = bound_opportunity_id;

    if (bound_contact_id) {
      input.contact_id = bound_contact_id;
    } else if (contact?.kind === "contact") {
      input.contact_id = contact.id;
    }

    on_submit(input);
  }

  return (
    <Modal
      open
      onClose={on_cancel}
      title={is_editing ? "Edit Sales Activity" : "Log Sales Activity"}
      size="md"
      footer={
        <>
          <button
            type="button"
            data-testid="composer-cancel"
            onClick={on_cancel}
            disabled={is_submitting}
            className="rounded-md border border-border-subtle px-3 py-1.5 text-sm text-text-primary hover:bg-recessed disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="activity-composer-form"
            data-testid="composer-submit"
            disabled={is_submitting}
            className="rounded-md bg-teal px-3 py-1.5 text-sm font-medium text-text-inverse hover:opacity-90 disabled:opacity-60"
          >
            {is_submitting
              ? (is_editing ? "Saving…" : "Logging…")
              : (is_editing ? "Save changes" : "Log activity")}
          </button>
        </>
      }
    >
      <form
        id="activity-composer-form"
        data-testid="activity-composer-modal"
        onSubmit={handleSubmit}
        className="flex flex-col gap-3"
      >
        {error && (
          <div
            role="alert"
            data-testid="composer-error"
            className="rounded-md bg-ember/15 px-3 py-2 text-sm text-ember"
          >
            {error}
          </div>
        )}

        <Field
          label="Type"
          hint={
            isContactEventType(type)
              ? "Completing this will also create a Touchpoint."
              : undefined
          }
        >
          <select
            data-testid="composer-type"
            value={type}
            onChange={(e) => setType(e.target.value as ActivityType)}
            disabled={is_editing}
            className="rounded-md border border-border-subtle bg-raised px-2 py-1.5 text-sm disabled:opacity-70"
          >
            {ACTIVITY_TYPE_META.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </Field>

        <Field label="Subject">
          <input
            data-testid="composer-subject"
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="e.g. Discovery call with VP HR"
            className="rounded-md border border-border-subtle bg-raised px-2 py-1.5 text-sm"
          />
        </Field>

        <Field label="Notes">
          <textarea
            data-testid="composer-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={3}
            className="rounded-md border border-border-subtle bg-raised px-2 py-1.5 text-sm"
          />
        </Field>

        <Field label="Due">
          <input
            data-testid="composer-due-at"
            type="datetime-local"
            value={dueAt}
            onChange={(e) => setDueAt(e.target.value)}
            className="rounded-md border border-border-subtle bg-raised px-2 py-1.5 text-sm"
          />
        </Field>

        {!is_editing && !is_anchor_bound && (
          <Field
            label="Anchor (Prospect or Opportunity)"
            hint={
              anchor?.kind === "opportunity"
                ? "Prospect is set from the opportunity automatically."
                : undefined
            }
          >
            <div data-testid="composer-target-picker">
              <TargetSearchPicker
                value={anchor}
                onChange={(next) => {
                  setAnchor(next);
                  // Clear contact when the anchor's prospect changes
                  // so a stale contact from a previous selection
                  // doesn't ride along to submit.
                  if (
                    contact && next?.kind !== "prospect"
                    && next?.parent_prospect_id !== contact.id
                  ) {
                    setContact(null);
                  }
                }}
                fetchResults={searchProspectsAndOpportunities}
                restrictTo={["prospect", "opportunity"]}
                placeholder="Search prospects or opportunities…"
              />
            </div>
          </Field>
        )}

        {!is_editing && !is_anchor_bound && !bound_contact_id
          && effective_prospect_id && (
          <Field
            label="Contact (optional)"
            hint="Typeahead restricted to this prospect's contacts."
          >
            <div data-testid="composer-contact-picker">
              <TargetSearchPicker
                value={contact}
                onChange={setContact}
                fetchResults={(q) =>
                  searchContactsForProspect(q, effective_prospect_id)
                }
                restrictTo={["contact"]}
                placeholder="Search this prospect's contacts…"
              />
            </div>
          </Field>
        )}
      </form>
    </Modal>
  );
}


function Field({
  label, hint, children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="font-medium text-text-primary">{label}</span>
      {children}
      {hint ? (
        <span className="text-xs text-text-tertiary">{hint}</span>
      ) : null}
    </label>
  );
}
